import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { logErr } from "../lib/utils-trel";
import ExperienceCard from "../components/ExperienceCard";
import { Sparkles, MapPin } from "lucide-react";

export default function PropertyConcierge(){
  const { slug } = useParams();
  const [prop, setProp] = useState(null);
  const [aiPrompt, setAiPrompt] = useState("");
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(()=>{ api.get(`/properties/${slug}`).then(r=>setProp(r.data)).catch((e)=>logErr("property fetch", e)); },[slug]);

  const ask = async () => {
    if (!aiPrompt || !prop?.city) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/plan", { request: aiPrompt, city: prop.city, budget: 500, guests: 2 });
      setPlan(data.plan);
    } catch (e) { logErr("AI plan error", e); }
    setLoading(false);
  };

  if (!prop) return <div className="p-20 text-center">Loading property…</div>;
  return (
    <div className="min-h-screen bg-[#FFF8EE]">
      <div className="relative h-[420px] md:h-[480px] overflow-hidden">
        <img src={prop.images?.[0]} className="absolute inset-0 w-full h-full object-cover" alt=""/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#071A2D] via-[#071A2D]/70 to-[#071A2D]/20"/>
        <div className="absolute bottom-10 left-6 right-6 max-w-5xl mx-auto text-[#FFF8EE]">
          <div className="text-[11px] uppercase tracking-[0.22em] mb-2 text-[#FFF8EE]" style={{textShadow:"0 2px 12px rgba(0,0,0,0.6)"}}>Welcome to</div>
          <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl leading-tight" data-testid="property-name" style={{textShadow:"0 4px 24px rgba(0,0,0,0.8)", color:"#FFF8EE"}}>{prop.name}</h1>
          <div className="text-sm mt-3 flex items-center gap-2 text-[#FFF8EE]" style={{textShadow:"0 2px 12px rgba(0,0,0,0.6)"}}><MapPin size={14}/> {prop.city}, {prop.state}</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="glass-ai rounded-3xl p-7">
          <div className="text-[#00AEEF] text-xs uppercase tracking-widest flex items-center gap-2"><Sparkles size={14}/> AI Concierge</div>
          <h2 className="font-serif text-2xl mt-2">What can we arrange for your stay?</h2>
          <textarea data-testid="property-ai-input" value={aiPrompt} onChange={e=>setAiPrompt(e.target.value)} rows={2}
            placeholder='"Romantic dinner on the beach tonight" · "Birthday surprise for tomorrow"'
            className="w-full bg-transparent text-lg outline-none mt-4 placeholder-[#071A2D]/40"/>
          <button onClick={ask} disabled={loading} className="btn-coral mt-3">{loading?"Designing…":"Ask concierge"}</button>
        </div>

        {plan && (
          <div className="mt-8 rounded-3xl bg-[#FFF8EE] border p-7 shadow-luxury">
            <h3 className="font-serif text-xl">{plan.title}</h3>
            <p className="text-sm text-[#071A2D]/70 mt-1">{plan.summary}</p>
            <div className="text-sm mt-2">~${plan.estimated_price} · {plan.duration}</div>
          </div>
        )}

        <h2 className="font-serif text-2xl mt-12 mb-5">Curated for nearby</h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {prop.nearby_experiences?.map(e=><ExperienceCard key={e.experience_id} exp={e} referralHost={prop.host_id}/>)}
        </div>

        <h2 className="font-serif text-2xl mt-12 mb-5">Amenities</h2>
        <div className="flex flex-wrap gap-2">
          {prop.amenities?.map(a=><span key={a} className="px-3 py-1.5 rounded-full bg-[#FFF8EE] border text-sm">{a}</span>)}
        </div>

        <div className="mt-12 p-6 rounded-2xl border border-dashed border-[#C9A45C] text-center">
          <div className="text-xs uppercase tracking-widest text-[#C9A45C]">QR Code</div>
          <div className="text-sm text-[#071A2D]/60 mt-2 font-mono break-all">{window.location.href}</div>
        </div>
      </div>
    </div>
  );
}
